import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { IBudgetEngagementListItem } from "dto/Budget/IBudgetEngagement";
import workflowService from "api/workflowService";
import acceptanceService from "api/acceptanceService";
import budgetService from "api/budgetService";
import { formatMoney } from "utils/formatMoney";
import { generateEngagementMetricPdf } from "utils/generateEngagementMetricPdf";
import SplitSlide, { IPreviewState } from "components/SplitSlide";
import PdfPreviewPane from "components/SplitSlide/PdfPreviewPane";
import EngagementDocumentPanel from "components/EngagementDocumentPanel";
import styles from "./styles.module.scss";

// Wspólne klucze proporcji/zoomu dla layoutu metryk (model 1 = SplitSlide).
const LS_SPLIT_KEY = "metric_slide_split_v1";
const LS_ZOOM_KEY = "metric_slide_zoom_v1";

interface ApprovalEvent {
  who: string;
  when: string;
  action: "submit" | "approve" | "reject" | "send_back" | "sign";
  stepName?: string;
}

type EngagementRow = IBudgetEngagementListItem & {
  approvalTimeline?: ApprovalEvent[];
  DivisionSymbol?: string;
  DivisionName?: string;
  ChapterName?: string;
  /** Osoba, która utworzyła zaangażowanie (jeśli brak — wnioskowane ze zdarzenia „submit"). */
  CreatedBy?: string;
};

interface Props {
  engagement: EngagementRow;
  onClose: () => void;
}

const ACTION_COLORS: Record<string, string> = {
  submit: "#5c8dc7",
  approve: "#2e7d32",
  reject: "#c62828",
  send_back: "#e65100",
  sign: "#4a3aa8",
};

const ACTION_LABELS: Record<string, string> = {
  submit: "Złożono wniosek",
  approve: "Zaakceptowano",
  reject: "Odrzucono",
  send_back: "Odesłano do poprawy",
  sign: "Podpisano elektronicznie",
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const EngagementMetricSlide = ({ engagement, onClose }: Props) => {
  const [generating, setGenerating] = useState(true);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleSection = (key: string) =>
    setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  // Szablon workflow i wykonany timeline akceptacji z BE (zamiast localStorage/mock).
  const template = workflowService.useActiveTemplate("engagement");
  const { data: beTimeline = [] } = acceptanceService.useGetApprovalTimeline(
    "engagement",
    engagement.Id,
  );
  const timeline: ApprovalEvent[] = engagement.approvalTimeline ?? beTimeline;
  // Bieżący przebieg w silniku workflow: na kim stoi etap (null = nie był w obiegu).
  const { data: instance } = workflowService.useInstance("engagement", engagement.Id);
  const currentStep =
    instance?.Status === "active"
      ? instance.Steps.find((s) => s.Index === instance.CurrentStepIndex)
      : undefined;
  // Dokument wniosku (XML WnioZaangBudz + HTML z BE) — gdy jest, to on jest podglądem
  // metryki (zamiast PDF składanego na FE) i nośnikiem podpisów.
  const { data: engagementDocument, isLoading: documentLoading } =
    budgetService.useGetEngagementDocument(engagement.Id);

  // Etap dla zdarzenia timeline: po nazwie etapu z migawki przebiegu (BE zwraca StepName),
  // dopiero potem z aktualnego szablonu. Zdarzenia „submit"/„sign" nie mają etapu —
  // dopasowanie po indeksie było błędne (formuła etapu 1 lądowała pod „Złożono wniosek").
  const stepForEvent = (ev: ApprovalEvent) => {
    if (!ev.stepName) return undefined;
    const fromInstance = instance?.Steps.find((s) => s.Name === ev.stepName);
    if (fromInstance)
      return {
        index: fromInstance.Index,
        name: fromInstance.Name ?? ev.stepName,
        documentFormula: fromInstance.DocumentFormula ?? undefined,
      };
    const fromTemplate = template?.steps.find((s) => s.name === ev.stepName);
    return fromTemplate
      ? {
          index: fromTemplate.order - 1,
          name: fromTemplate.name,
          documentFormula: fromTemplate.documentFormula,
        }
      : { index: undefined, name: ev.stepName, documentFormula: undefined };
  };

  const chapterSymbol = engagement.ChapterSymbol ?? "";
  const divisionSymbol = engagement.DivisionSymbol ?? chapterSymbol.slice(0, 3);
  const divisionName = engagement.DivisionName ?? "";
  const chapterName = engagement.ChapterName ?? "";
  const createdBy =
    engagement.CreatedBy ??
    timeline.find((e) => e.action === "submit")?.who ??
    "";

  // Model 1 (SplitSlide): podgląd po lewej jest ZAWSZE widoczny, więc metrykę
  // generujemy automatycznie po otwarciu (bez przycisku „PDF").
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current || documentLoading) return;
    if (engagementDocument) {
      // Jest dokument wniosku z BE — PDF z FE niepotrzebny.
      setGenerating(false);
      return;
    }
    loadedRef.current = true;
    (async () => {
      try {
        const blob = await generateEngagementMetricPdf({
          title: engagement.Title ?? "",
          signature: engagement.Signature,
          engagementDate: engagement.EngagementDate ?? new Date().toISOString(),
          executionDeadline:
            engagement.ExecutionDeadline ?? new Date().toISOString(),
          grantingProcedure: engagement.GrantingProcedure ?? "",
          amount: engagement.Amount ?? 0,
          divisionSymbol,
          divisionName,
          chapterSymbol,
          chapterName,
          timeline: timeline.map((ev) => {
            const step = stepForEvent(ev);
            return {
              who: ev.who,
              when: ev.when,
              action: ev.action,
              stepName: step?.name,
              documentFormula: step?.documentFormula,
            };
          }),
        });
        setPdfBlob(blob);
      } catch {
        setPdfError("Nie udało się przygotować podglądu metryki.");
      } finally {
        setGenerating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentLoading, engagementDocument]);

  const previewState: IPreviewState | null = documentLoading
    ? { text: "Ładowanie dokumentu wniosku…" }
    : engagementDocument
    ? null
    : generating
    ? { text: "Generowanie podglądu metryki…" }
    : pdfError
    ? { text: pdfError, isError: true }
    : !pdfBlob
    ? { text: "Brak podglądu metryki." }
    : null;

  return (
    <SplitSlide
      title="Metryka zaangażowania środków"
      splitStorageKey={LS_SPLIT_KEY}
      devTodoKey="slide:engagement-metric"
      onClose={onClose}
      previewState={previewState}
      preview={
        engagementDocument ? (
          <iframe
            className={styles.documentPreview}
            title="Wniosek o zaangażowanie środków budżetowych"
            sandbox=""
            srcDoc={engagementDocument.Html}
          />
        ) : (
          pdfBlob && (
            <PdfPreviewPane
              file={pdfBlob}
              title="Metryka zaangażowania"
              zoomStorageKey={LS_ZOOM_KEY}
            />
          )
        )
      }
    >
      <div className={styles.body}>
        {/* Klasyfikacja budżetowa + dane — jedna ramka, sekcja zwijana */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("info")}
        >
          <span>Klasyfikacja budżetowa</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.info ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.info && (
          <div className={styles.metaCard}>
            <div className={styles.metaTitleRow}>
              <div className={styles.metaTitleLabel}>Tytuł</div>
              <div className={styles.metaTitleValue}>{engagement.Title}</div>
            </div>
            <div className={styles.metaGrid}>
              <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                <span className={styles.metaLabel}>Dział</span>
                <span className={styles.metaValue}>
                  {divisionSymbol}
                  {divisionName ? ` — ${divisionName}` : ""}
                </span>
              </div>
              <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                <span className={styles.metaLabel}>Rozdział</span>
                <span className={styles.metaValue}>
                  {chapterSymbol}
                  {chapterName ? ` — ${chapterName}` : ""}
                </span>
              </div>
              {engagement.Signature && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Sygnatura</span>
                  <span className={styles.metaValue}>
                    {engagement.Signature}
                  </span>
                </div>
              )}
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Kwota</span>
                <span className={styles.metaValue}>
                  {formatMoney(engagement.Amount ?? 0)}
                </span>
              </div>
              {engagement.EngagementDate && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Data zaangażowania</span>
                  <span className={styles.metaValue}>
                    {fmtDate(engagement.EngagementDate)}
                  </span>
                </div>
              )}
              {engagement.ExecutionDeadline && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Termin realizacji</span>
                  <span className={styles.metaValue}>
                    {fmtDate(engagement.ExecutionDeadline)}
                  </span>
                </div>
              )}
              {engagement.GrantingProcedure && (
                <div className={styles.metaItem} style={{ gridColumn: "1" }}>
                  <span className={styles.metaLabel}>Tryb zamówienia</span>
                  <span className={styles.metaValue}>
                    {engagement.GrantingProcedure}
                  </span>
                </div>
              )}
              <div className={styles.metaItem} style={{ gridColumn: "2" }}>
                <span className={styles.metaLabel}>
                  Zaangażowanie utworzył/ła
                </span>
                <span className={styles.metaValue}>{createdBy || "—"}</span>
              </div>
            </div>
          </div>
        )}

        {/* Dokument wniosku: podpisy elektroniczne + podpis własnych elementów */}
        {engagementDocument && (
          <>
            <div
              className={styles.sectionTitle}
              onClick={() => toggleSection("document")}
            >
              <span>Podpisy dokumentu wniosku</span>
              <FontAwesomeIcon
                className={styles.sectionChevron}
                icon={collapsed.document ? faChevronRight : faChevronDown}
              />
            </div>
            {!collapsed.document && (
              <div className={styles.metaCard}>
                <EngagementDocumentPanel
                  engagementId={engagement.Id}
                  showPreview={false}
                />
              </div>
            )}
          </>
        )}

        {/* Timeline — sekcja zwijana */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("timeline")}
        >
          <span>Przebieg procedury akceptacji</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.timeline ? faChevronRight : faChevronDown}
          />
        </div>

        {!collapsed.timeline && currentStep && (
          <div className={styles.noTimeline}>
            Etap w toku: <b>{currentStep.Name || `Etap ${currentStep.Index + 1}`}</b>
            {" — czeka na: "}
            {currentStep.Assignments.filter((a) => a.Decision === "pending")
              .map((a) => a.AssigneeLabel ?? "—")
              .join(", ") || "—"}
          </div>
        )}
        {!collapsed.timeline && instance && instance.Status !== "active" && (
          <div className={styles.noTimeline}>
            Procedura:{" "}
            {instance.Status === "completed"
              ? "zakończona — zaakceptowane"
              : instance.Status === "rejected"
              ? "odrzucona"
              : instance.Status === "returned"
              ? "zwrócona do poprawy"
              : "anulowana"}
            {instance.Steps.flatMap((s) => s.Assignments)
              .filter((a) => a.Comment && a.Decision !== "pending")
              .map((a) => ` · ${a.AssigneeLabel}: „${a.Comment}”`)
              .join("")}
          </div>
        )}

        {!collapsed.timeline &&
          (timeline.length === 0 ? (
            <div className={styles.noTimeline}>
              Brak zarejestrowanych zdarzeń w procedurze akceptacji
            </div>
          ) : (
            <div className={styles.steps}>
              {timeline.map((ev, i) => {
                const step = stepForEvent(ev);
                const stepName =
                  step?.name ?? ACTION_LABELS[ev.action] ?? ev.action;
                const color = ACTION_COLORS[ev.action] ?? "#888";
                const isLast = i === timeline.length - 1;

                return (
                  <div key={i} className={styles.step}>
                    <div className={styles.stepLine}>
                      <div
                        className={styles.stepDot}
                        style={{ background: color }}
                      />
                      {!isLast && <div className={styles.stepConnector} />}
                    </div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepNumber}>
                        {step?.index !== undefined
                          ? `Etap ${step.index + 1}`
                          : ACTION_LABELS[ev.action] ?? ev.action}
                      </div>
                      <div className={styles.stepName}>{stepName}</div>
                      {step?.documentFormula?.trim() && (
                        <div className={styles.formulaBlock}>
                          {step.documentFormula}
                        </div>
                      )}
                      <div className={styles.stepMeta}>
                        <span className={styles.stepMetaLabel}>Osoba:</span>
                        <span className={styles.stepMetaValue}>{ev.who}</span>
                        <span className={styles.stepMetaLabel}>Data:</span>
                        <span className={styles.stepMetaValue}>
                          {fmtDateTime(ev.when)}
                        </span>
                        <span className={styles.stepMetaLabel}>Wynik:</span>
                        <span
                          className={styles.actionLabel}
                          style={{ color, background: `${color}18` }}
                        >
                          {ACTION_LABELS[ev.action] ?? ev.action}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
      </div>
    </SplitSlide>
  );
};

export default EngagementMetricSlide;
